import { useState, useCallback, useEffect, useMemo } from "react";
import { Form } from "@dreambigger/design-system/src/components";
import { Checkbox, Radio } from "antd";
import {
  PlaidAccount,
  PlaidAuthStepAssets,
} from "@dreambigger/shared/src/types";
import { StepProps } from "../pages/flows/[flowId]";
import styles from "./steps.module.scss";
import StepWrapper from "./step-wrapper";
import { usePlaid, useApplication } from "../api";
import { Button } from "@dreambigger/design-system/src/components";
import { ArrowRightOutlined } from "@ant-design/icons";

import { usePlaidLink, PlaidLinkOnSuccess } from "react-plaid-link";
import { useRouter } from "next/router";

const accountUuidKey = "financialPlaidAccountUuid";

const getFirstValue = (val: string | string[]) =>
  Array.isArray(val) ? val[0] : val;
export const confirmationKey = "accountConfirmation";

export default function PlaidAuth({ flow, step, brand, progress }: StepProps) {
  const assets: PlaidAuthStepAssets = step.assets;
  const {
    institutionName,
    linkCustomizationName,
    ctaText,
    confirmationText,
    helpText1,
    helpText2,
    termsText,
    noUsableAccountsText,
  } = assets;

  const router = useRouter();
  const { token: tokenParam, received_redirect_uri: receivedRedirectUriParam } =
    router.query;

  // state
  const [token, setToken] = useState<string | null>(null);
  const [receivedRedirectUri, setReceivedRedirectUri] = useState<string>();
  const [disabled, setDisabled] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [accountUuid, setAccountUuid] = useState<string | null>(null);
  const [callbackMode, setCallbackMode] = useState<boolean>(false);
  const [availableAccounts, setAvailableAccounts] = useState<PlaidAccount[]>(
    []
  );
  // Gates the "no usable accounts" message so it only appears after the user
  // has actually attempted a link — not on initial mount when the radio group
  // is naturally empty.
  const [hasLinkedOnce, setHasLinkedOnce] = useState(false);

  // Filter out accounts Plaid returned without ACH numbers — these can't be
  // used for ACH funding downstream, so we hide them from the selection UI.
  const usableAccounts = useMemo(
    () => availableAccounts.filter((account) => account.hasAchNumbers),
    [availableAccounts]
  );
  const showNoUsableAccountsMessage =
    hasLinkedOnce && !processing && usableAccounts.length === 0;

  // callbacks
  const onSuccess = useCallback<PlaidLinkOnSuccess>((publicToken) => {
    const linkAccount = async () => {
      setProcessing(true);
      const accounts =
        (await plaidHelper.linkAuthAccount({
          publicToken,
        })) || [];
      // Pre-select the first usable account
      const firstUsable = accounts.find((account) => account.hasAchNumbers);
      setAccountUuid(firstUsable?.uuid ?? null);
      setAvailableAccounts(accounts);
      setHasLinkedOnce(true);
      setProcessing(false);
    };

    setAccountUuid(null);
    linkAccount();
  }, []);

  const handleClick = () => launchPlaid();

  // custom hooks
  const [form] = Form.useForm();
  const plaidHelper = usePlaid(flow.financialInstitution.id);
  const applicationHelper = useApplication(flow.financialInstitution.id);
  // The account the applicant previously selected on this step (if any), read
  // straight from the saved response. Used to restore their choice when they
  // return to the step later (see restore effect below).
  const savedAccountUuid = useMemo(
    () =>
      applicationHelper.findResponse(flow.id, "draft", step.slug)?.fields?.[
        accountUuidKey
      ] as string | undefined,
    [applicationHelper, flow.id, step.slug]
  );
  const { open: launchPlaid, ready: plaidIsReady } = usePlaidLink({
    token,
    receivedRedirectUri,
    onSuccess,
  });

  // use effect
  // Restore the previously-saved account when the applicant returns to this
  // step. The form's `initialValues` prefill is applied only at mount and is
  // not reactive, so once the application loads (after mount) the saved
  // `financialPlaidAccountUuid` would otherwise never reach state/the form,
  // leaving the step empty. An empty submit skips the PATCH in step-wrapper,
  // which strands `lastVisitedSlug` on the prior step and lets the visitedPath
  // skip-gap rule prune this (genuinely-visited) step. Restoring the saved
  // value guarantees the step re-records itself on submit.
  //
  // Gated on `!hasLinkedOnce` so a fresh link session the applicant *completes*
  // overrides the saved selection, while one they *abandon* falls back to it.
  useEffect(() => {
    if (savedAccountUuid && !hasLinkedOnce) {
      setAccountUuid(savedAccountUuid);
    }
  }, [savedAccountUuid, hasLinkedOnce]);

  const checkDisabled = () => {
    setDisabled(!accountUuid);
    if (!accountUuid) {
      setDisabled(true);
      return;
    }
    if (confirmationText) {
      const checked = form.getFieldValue(confirmationKey);
      if (!checked) {
        setDisabled(true);
        return;
      }
    }
    setDisabled(false);
  };

  useEffect(() => {
    checkDisabled();
  }, [accountUuid]);

  useEffect(() => {
    form.setFieldsValue({ [accountUuidKey]: accountUuid });
  }, [accountUuid, form]);

  useEffect(() => {
    if (tokenParam && receivedRedirectUriParam) {
      // plaid in callback mode
      setCallbackMode(true);
      setToken(getFirstValue(tokenParam));
      setReceivedRedirectUri(
        decodeURIComponent(getFirstValue(receivedRedirectUriParam))
      );
    } else {
      // get link_token from your server when component mounts
      const createLinkToken = async () => {
        setProcessing(true);

        const { linkToken } = await plaidHelper.createLinkToken({
          institutionName: institutionName,
          linkCustomizationName: linkCustomizationName,
          redirectUri: `${window.location.origin}/plaid/oauth/${flow.financialInstitution.id}`,
          options: {
            returnUrl: window.location.href,
          },
        });

        setToken(linkToken);
        setProcessing(false);
      };

      createLinkToken();
    }
  }, []);

  useEffect(() => {
    if (plaidIsReady && callbackMode) {
      launchPlaid();
    }
  }, [plaidIsReady]);

  const wrapperProps = {
    step,
    brand,
    flow,
    progress,
    form,
    disabled,
    processing,
  };

  return (
    <StepWrapper {...wrapperProps}>
      {helpText1 && (
        <div>
          <p
            className={`${styles.textLinks} f-4 gray-8 lh-4 mb-6`}
            dangerouslySetInnerHTML={{
              __html: helpText1 ?? "",
            }}
          />
        </div>
      )}

      {/*
        Keep this Form.Item *mounted* even when there are no usable accounts to
        render. antd's `onFinish` only collects values from registered Form.Items;
        if this is conditionally unmounted, the previously-saved
        `financialPlaidAccountUuid` (written into the form via setFieldsValue
        from the restored React state) never reaches submit. The empty-fields
        early-return in step-wrapper then skips the PATCH, `lastVisitedSlug`
        stays on the prior step, and the visitedPath skip-gap rule prunes this
        step. `hidden` keeps the field registered and value-tracked while still
        hiding the radio list visually.
      */}
      <Form.Item
        name={accountUuidKey}
        hidden={processing || usableAccounts.length === 0}
      >
        <Radio.Group className="w-100" defaultValue={accountUuid}>
          {usableAccounts.map((availableAccount, key) => {
            return (
              <div
                key={`container-${availableAccount.uuid}`}
                className={`pt-2 ${
                  key !== usableAccounts.length - 1 ? "bb" : ""
                }`}
                style={{ borderColor: "#bfbfbf" }}
              >
                <Radio
                  key={availableAccount.uuid}
                  value={availableAccount.uuid} // radio values must always be strings.
                  className="pa-2 ao-lg-radio lightenPrimaryBkd lightenPrimaryBkdChecked w-100 subtleLift br-4 mb-2 checkedFill"
                  onChange={() => setAccountUuid(availableAccount.uuid)}
                >
                  <div className="f-5">
                    {availableAccount.item?.institution?.name &&
                      `${availableAccount.item.institution.name} - `}
                    {availableAccount.name}
                  </div>
                  {availableAccount.mask && (
                    <div className="f-2 mt-1">
                      Account ending in {availableAccount.mask}
                    </div>
                  )}
                </Radio>
              </div>
            );
          })}
        </Radio.Group>
      </Form.Item>

      {showNoUsableAccountsMessage && (
        <div
          className={`${styles.textLinks} f-4 lh-4 mb-6`}
          style={{ color: "#cf1322" }}
          dangerouslySetInnerHTML={{
            __html:
              noUsableAccountsText ??
              "We weren't able to get the account details we need from your bank. Please try linking a different bank.",
          }}
        />
      )}

      <Form.Item>
        <Button type="dashed" onClick={handleClick} disabled={!plaidIsReady}>
          {ctaText || "Connect"} <ArrowRightOutlined />
        </Button>
      </Form.Item>

      {helpText2 && (
        <div>
          <p
            className={`${styles.textLinks} f-4 gray-8 lh-4 mb-6`}
            dangerouslySetInnerHTML={{
              __html: helpText2 ?? "",
            }}
          />
        </div>
      )}
      {confirmationText && (
        <div className="flex items-center">
          <Form.Item
            name={confirmationKey}
            valuePropName="checked"
            className="mr-3 mb-6"
          >
            <Checkbox className="ao-lg-checkbox" onChange={checkDisabled} />
          </Form.Item>
          <p
            className={`${styles.textLinks} f-4 gray-8 lh-4 mb-6`}
            dangerouslySetInnerHTML={{
              __html: confirmationText,
            }}
          />
        </div>
      )}
      {termsText && (
        <div>
          <p
            className={`${styles.textLinks} f-4 gray-8 lh-4 mb-6`}
            dangerouslySetInnerHTML={{
              __html: termsText ?? "",
            }}
          />
        </div>
      )}
    </StepWrapper>
  );
}
